import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import api from "../../../../api";
import { useAuth } from "../../../../context/AuthContext";
import {
  getDrugBySku,
  getPrescriptionMedications,
} from "../../../../api/dispensary/script/LabelApi";

const getBranchByCode = async (branchcode) => {
  try {
    const response = await api.get(`/api/branches/${branchcode}`);
    return response.data;
  } catch (err) {
    console.error("Error in getBranchByCode service:", err);
    throw err;
  }
};

export default function LabelConfigurationStateProvider({ children }) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const prescriptionId = searchParams.get("prescriptionId");
  const urlIcd10Codes = searchParams.get("icd10Codes");

  const location = useLocation();
  const rawNavigationState = location.state;

  const isFromDispensaryMenu = !rawNavigationState && !prescriptionId;

  const navigationState = useMemo(() => {
    if (!rawNavigationState) return null;
    try {
      return JSON.parse(JSON.stringify(rawNavigationState));
    } catch {
      return rawNavigationState;
    }
  }, [rawNavigationState]);

  const [forms, setForms] = useState(navigationState?.labelData || []);
  const [pharmacyInfo, setPharmacyInfo] = useState({
    name: navigationState?.pharmacyInfo?.name || "DURBAN NORTH MEDICAL CENTRE",
    address:
      navigationState?.pharmacyInfo?.address ||
      "35 UMHLANGA ROCKS DRIVE, DURBAN NORTH, 4051",
    phone: navigationState?.pharmacyInfo?.phone || "031 563 7577",
  });

  const [activeTab, setActiveTab] = useState("Item Label");
  const [activePreviewId, setActivePreviewId] = useState(null);
  const [drugInfo, setDrugInfo] = useState(null);

  const [showRepeat, setShowRepeat] = useState(false);
  const [repeatData, setRepeatData] = useState(null);

  const receiptNumber = navigationState?.receiptNumber || null;
  const totals = navigationState?.totals || null;
  const scriptNumber = String(navigationState?.scriptNumber || "");

  const repeatNumberFromNav =
    navigationState?.repeatNumber ??
    navigationState?.baseState?.repeatNumber ??
    navigationState?.scriptData?.repeatNumber ??
    null;

  const effectiveShowRepeat = Boolean(
    showRepeat ||
      (repeatData &&
        (Number(repeatData.repeatsTotal) > 0 ||
          String(repeatData.displayText || "").trim().length > 0))
  );

  const updateFormsWithInventoryBarcodes = async (formsToUpdate) => {
    return formsToUpdate;
  };

  useEffect(() => {
    if (navigationState?.labelData && navigationState?.labelData.length > 0) {
      if (prescriptionId) {
        const fetchCompleteData = async () => {
          try {
            const response = await getPrescriptionMedications(prescriptionId);
            if (response.success && response.data) {
              const urlIcd10Array = urlIcd10Codes
                ? urlIcd10Codes.split(",")
                : [];

              const drugForms = response.data.map((drug, index) => {
                return {
                  id: uuidv4(),
                  data: {
                    patientName: drug.patientName || "",
                    medication: drug.name || "",
                    quantity: drug.quantity || "",
                    prescriber: drug.pharmacistName || drug.practitioner || "",
                    rxNumber: String(drug.rxNumber || ""),
                    directions:
                      drug.prescriptionDirections || drug.directions || "",
                    packSize: drug.packSize || "",
                    icd10:
                      urlIcd10Array[index] ||
                      (drug.icd10Codes
                        ? drug.icd10Codes.join(", ")
                        : drug.icd10 || ""),
                    practitioner: drug.practitionerName
                      ? {
                          title:
                            drug.practitionerName.split(" ").length > 2
                              ? drug.practitionerName.split(" ")[0]
                              : "",
                          firstname:
                            drug.practitionerName.split(" ").length > 2
                              ? drug.practitionerName
                                  .split(" ")
                                  .slice(1, -1)
                                  .join(" ")
                              : drug.practitionerName
                                  .split(" ")
                                  .slice(0, -1)
                                  .join(" ") || "",
                          surname:
                            drug.practitionerName.split(" ").slice(-1)[0] ||
                            "",
                        }
                      : {},
                    barcode: "",
                    date: new Date().toISOString().split("T")[0],
                  },
                  isDrug: drug.isDrug,
                };
              });

              const formsWithInventoryBarcodes =
                await updateFormsWithInventoryBarcodes(drugForms);
              setForms(formsWithInventoryBarcodes);
            }
          } catch (error) {
            console.error("Error fetching complete prescription data:", error);
            const updateWithInventoryBarcodes = async () => {
              const formsWithInventoryBarcodes =
                await updateFormsWithInventoryBarcodes(navigationState.labelData);
              setForms(formsWithInventoryBarcodes);
            };
            updateWithInventoryBarcodes();
          }
        };

        fetchCompleteData();
        return;
      }

      const updateWithInventoryBarcodes = async () => {
        const formsWithInventoryBarcodes =
          await updateFormsWithInventoryBarcodes(navigationState.labelData);
        setForms(formsWithInventoryBarcodes);
      };

      updateWithInventoryBarcodes();
      return;
    }

    if (prescriptionId) {
      const fetchPrescriptionData = async () => {
        try {
          const response = await getPrescriptionMedications(prescriptionId);
          const urlIcd10Array = urlIcd10Codes ? urlIcd10Codes.split(",") : [];

          const drugForms = response.data.map((drug, index) => {
            return {
              id: uuidv4(),
              data: {
                patientName: drug.patientName || "",
                medication: drug.name || "",
                quantity: drug.quantity || "",
                prescriber: drug.pharmacistName || drug.practitioner || "",
                rxNumber: String(drug.rxNumber || ""),
                directions: drug.prescriptionDirections || drug.directions || "",
                packSize: drug.packSize || "",
                icd10:
                  urlIcd10Array[index] ||
                  (drug.icd10Codes
                    ? drug.icd10Codes.join(", ")
                    : drug.icd10 || ""),
                practitioner: drug.practitionerName
                  ? {
                      title:
                        drug.practitionerName.split(" ").length > 2
                          ? drug.practitionerName.split(" ")[0]
                          : "",
                      firstname:
                        drug.practitionerName.split(" ").length > 2
                          ? drug.practitionerName
                              .split(" ")
                              .slice(1, -1)
                              .join(" ")
                          : drug.practitionerName
                              .split(" ")
                              .slice(0, -1)
                              .join(" ") || "",
                      surname:
                        drug.practitionerName.split(" ").slice(-1)[0] || "",
                    }
                  : {},
                barcode: "",
                date: new Date().toISOString().split("T")[0],
              },
              isDrug: drug.isDrug,
              isTracer: activeTab === "Tracer Label" && index === 0,
            };
          });

          const formsWithInventoryBarcodes =
            await updateFormsWithInventoryBarcodes(drugForms);
          setForms(formsWithInventoryBarcodes);
        } catch (error) {
          console.error("Error fetching prescription drugs:", error);
          setForms([
            {
              id: uuidv4(),
              data: {
                patientName: "",
                medication: "",
                quantity: "",
                prescriber: "",
                rxNumber: "",
                directions: "",
                packSize: "",
                icd10: "",
                practitioner: "",
                barcode: "",
                date: new Date().toISOString().split("T")[0],
              },
              isDrug: true,
            },
          ]);
        }
      };

      fetchPrescriptionData();
    } else {
      const loggedInUserName =
        user?.name && user?.surname
          ? `${user.name} ${user.surname}`
          : user?.username || "";

      setForms([
        {
          id: uuidv4(),
          data: {
            patientName: "",
            medication: "",
            quantity: "",
            prescriber: loggedInUserName,
            rxNumber: "",
            directions: "",
            packSize: "",
            icd10: "",
            practitioner: "",
            barcode: "",
            date: new Date().toISOString().split("T")[0],
          },
          isDrug: true,
        },
      ]);
    }
  }, [prescriptionId, urlIcd10Codes, navigationState?.labelData]);

  const fetchRepeatData = async (prescriptionIdValue) => {
    if (!prescriptionIdValue) {
      return null;
    }

    try {
      const locationState = location.state || {};
      const scriptData = locationState.scriptData || {};
      const baseState = locationState.baseState || {};
      const nestedScriptData = baseState.scriptData || {};

      const repeatId =
        baseState.repeatId ||
        nestedScriptData.repeatId ||
        scriptData.repeatId ||
        scriptData.repeatid;

      if (repeatId) {
        try {
          const response = await api.get(
            `/scripts/repeats/${encodeURIComponent(repeatId)}/details`
          );

          if (response.data && response.data.success) {
            const repeatDetails = response.data.data;

            const repeatInfo = {
              repeatsFulfilled:
                repeatDetails.repeatsFulfilled || repeatDetails.fulfilled || 0,
              repeatsTotal:
                repeatDetails.repeatsTotal ||
                repeatDetails.total ||
                repeatDetails.repeats ||
                0,
              repeatsDueCount: repeatDetails.repeatsDueCount || 0,
              displayText:
                repeatDetails.repeat ||
                repeatDetails.displayText ||
                `${
                  repeatDetails.repeatsFulfilled || repeatDetails.fulfilled || 0
                } OF ${
                  repeatDetails.repeatsTotal ||
                  repeatDetails.total ||
                  repeatDetails.repeats ||
                  0
                }`,
            };

            if (repeatInfo.repeatsTotal > 0) {
              setRepeatData(repeatInfo);
              setShowRepeat(true);
              return repeatInfo;
            }

            setRepeatData(null);
            setShowRepeat(false);
            return null;
          }
        } catch (error) {
          console.error("[LabelConfiguration] Error fetching repeat details:", error);
        }
      }

      const repeatInfo = {
        repeatsFulfilled:
          baseState.repeatsFulfilled || nestedScriptData.repeatsFulfilled || 0,
        repeatsTotal:
          baseState.repeatsTotal ||
          nestedScriptData.repeatsTotal ||
          baseState.repeats ||
          nestedScriptData.repeats ||
          0,
        repeatsDueCount:
          baseState.repeatsDueCount || nestedScriptData.repeatsDueCount || 0,
        displayText:
          baseState.repeat ||
          nestedScriptData.repeat ||
          `${
            baseState.repeatsFulfilled || nestedScriptData.repeatsFulfilled || 0
          } OF ${
            baseState.repeatsTotal ||
            nestedScriptData.repeatsTotal ||
            baseState.repeats ||
            nestedScriptData.repeats ||
            0
          }`,
      };

      if (repeatInfo.repeatsTotal > 0) {
        setRepeatData(repeatInfo);
        setShowRepeat(true);
        return repeatInfo;
      }

      setRepeatData(null);
      setShowRepeat(false);
      return null;
    } catch (error) {
      console.error("[LabelConfiguration] Error fetching repeat data:", error);
      setRepeatData(null);
      setShowRepeat(false);
      return null;
    }
  };

  useEffect(() => {
    if (prescriptionId) {
      fetchRepeatData(prescriptionId);
    } else {
      setRepeatData(null);
      setShowRepeat(false);
    }
  }, [prescriptionId]);

  useEffect(() => {
    const fetchBranchInfo = async () => {
      if (user?.branchcode) {
        try {
          const response = await getBranchByCode(user.branchcode);

          if (response && response.data) {
            const branchData = response.data;
            setPharmacyInfo({
              name: branchData.branchName || branchData.name || "Unknown Branch",
              address:
                branchData.address ||
                branchData.branchAddress ||
                "No address available",
              phone:
                branchData.phone ||
                branchData.cellphone ||
                branchData.contactNumber ||
                "No phone available",
            });
          }
        } catch (error) {
          console.error("Error fetching branch information:", error);
        }
      }
    };

    fetchBranchInfo();
  }, [user?.branchcode]);

  const handleInputChange = async (formId, field, value) => {
    setForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? { ...form, data: { ...form.data, [field]: value } }
          : form
      )
    );
  };

  const handlePharmacyChange = (field, value) => {
    setPharmacyInfo((prev) => ({ ...prev, [field]: value }));
  };

  const addForm = () => {
    const loggedInUserName =
      user?.name && user?.surname
        ? `${user.name} ${user.surname}`
        : user?.username || "";

    setForms((prev) => [
      ...prev,
      {
        id: uuidv4(),
        data: {
          patientName: "",
          medication: "",
          quantity: "",
          prescriber: loggedInUserName,
          rxNumber: "",
          directions: "",
          packSize: "",
          icd10: "",
          practitioner: "",
          barcode: "",
          date: new Date().toISOString().split("T")[0],
        },
        isDrug: true,
      },
    ]);
  };

  const removeForm = (formId) => {
    setForms((prev) => prev.filter((form) => form.id !== formId));
    setActivePreviewId((prev) => (prev === formId ? null : prev));
  };

  const fetchDrugInfoForPreview = async (formId) => {
    const form = forms.find((f) => f.id === formId);
    if (!form?.data?.sku) {
      setDrugInfo({
        strength: null,
        dosageForm: null,
        schedule: null,
        packSize: "",
      });
      return;
    }

    try {
      const result = await getDrugBySku(form.data.sku);

      if (result.success) {
        const newDrugInfo = {
          strength: result.data.strength,
          dosageForm: result.data.dosageform,
          schedule: result.data.schedule,
          packSize: result.data.packsize || "",
        };

        setDrugInfo(newDrugInfo);

        setForms((prev) =>
          prev.map((f) =>
            f.id === formId
              ? {
                  ...f,
                  data: {
                    ...f.data,
                    strength: newDrugInfo.strength,
                    dosageForm: newDrugInfo.dosageForm,
                    packSize: newDrugInfo.packSize,
                    schedule: newDrugInfo.schedule,
                    drugInfo: newDrugInfo,
                  },
                }
              : f
          )
        );
      } else {
        setDrugInfo({
          strength: null,
          dosageForm: null,
          schedule: null,
          packSize: "",
        });
      }
    } catch (error) {
      console.error("Error fetching drug info for preview:", error);
      setDrugInfo({
        strength: null,
        dosageForm: null,
        schedule: null,
        packSize: "",
      });
    }
  };

  const handleDrugInfoUpdate = (formId, newDrugInfo) => {
    setForms((prev) =>
      prev.map((form) =>
        form.id === formId
          ? {
              ...form,
              data: {
                ...form.data,
                strength: newDrugInfo.strength,
                dosageForm: newDrugInfo.dosageForm,
                packSize: newDrugInfo.packSize,
                schedule: newDrugInfo.schedule,
                drugInfo: newDrugInfo,
              },
            }
          : form
      )
    );
  };

  return children({
    user,
    prescriptionId,
    urlIcd10Codes,
    location,
    navigationState,
    isFromDispensaryMenu,
    forms,
    setForms,
    pharmacyInfo,
    setPharmacyInfo,
    activeTab,
    setActiveTab,
    activePreviewId,
    setActivePreviewId,
    drugInfo,
    setDrugInfo,
    showRepeat,
    setShowRepeat,
    repeatData,
    setRepeatData,
    effectiveShowRepeat,
    repeatNumberFromNav,
    receiptNumber,
    totals,
    scriptNumber,
    updateFormsWithInventoryBarcodes,
    handleInputChange,
    handlePharmacyChange,
    addForm,
    removeForm,
    fetchDrugInfoForPreview,
    handleDrugInfoUpdate,
  });
}
